import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

function parseJwtRoles(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Array.isArray(payload?.roles) ? payload.roles : ['USER'];
  } catch {
    return ['USER'];
  }
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount, and process OAuth callback if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthStatus = urlParams.get('oauth');
    const token = urlParams.get('token');
    const username = urlParams.get('username');
    const email = urlParams.get('email');

    if (oauthStatus === 'success' && token) {
      const decodedToken = decodeURIComponent(token);
      sessionStorage.setItem('authToken', decodedToken);
      const roles = parseJwtRoles(decodedToken);
      sessionStorage.setItem('userInfo', JSON.stringify({
        username: decodeURIComponent(username || ''),
        email: decodeURIComponent(email || ''),
        roles
      }));
      window.history.replaceState({}, document.title, window.location.pathname);

      // If Google OAuth was triggered from the consultation booking flow, redirect back
      if (sessionStorage.getItem('pendingConsultationPayment') === 'true') {
        window.location.replace('/consultation');
        return;
      }
    } else if (oauthStatus === 'error') {
      console.error('OAuth error:', urlParams.get('message'));
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = () => {
    const currentUser = authService.getCurrentUser();
    const authenticated = authService.isAuthenticated();
    setUser(currentUser);
    setIsAuthenticated(authenticated);
    setLoading(false);
  };

  const signIn = async (credentials) => {
    const response = await authService.signIn(credentials);
    checkAuth(); // Update auth state after sign in
    return response;
  };

  const signUp = async (userData) => {
    const response = await authService.signUp(userData);
    checkAuth(); // Update auth state after sign up
    return response;
  };

  const signOut = useCallback(() => {
    authService.signOut();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    const handleExpired = () => signOut();
    window.addEventListener('auth:session-expired', handleExpired);
    return () => window.removeEventListener('auth:session-expired', handleExpired);
  }, [signOut]);

  const value = {
    user,
    isAuthenticated,
    loading,
    signIn,
    signUp,
    signOut,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
